// Routing and the app shell.
//
// Both the navigation and the route guards come from the permission map in
// shared/permissions.js. Nothing here compares a role to a string, so adding a
// role is an edit to that map and nothing else.
//
// The guards decide what is worth rendering. They are not the security
// boundary: every route below is also enforced on the server, so typing a path
// into the address bar gets a 403 from the API regardless of what this file
// allows.

import { Suspense, lazy } from 'react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { viewForPath } from '../../shared/permissions.js'
import {
  Building2,
  CalendarCheck,
  FileBarChart,
  FlaskConical,
  Landmark,
  Moon,
  Rows3,
  Settings as SettingsIcon,
  Sun,
  Users as UsersIcon,
} from 'lucide-react'
import { useAuth } from './auth.jsx'
import { useTheme } from './theme/ThemeProvider.jsx'
import OnsitesOpt from './OnsitesOpt.jsx'
import ViewAs from './ViewAs.jsx'
import { ConfirmProvider } from './confirm.jsx'
import { AppShell, Button, Notice } from './components/ui/index.js'
import SignIn from './pages/SignIn.jsx'
import Customers from './pages/Customers.jsx'
import Activity from './pages/Activity.jsx'
import ActivityCustomer from './pages/ActivityCustomer.jsx'
import ActivityImports from './pages/ActivityImports.jsx'
import ActivityImportReview from './pages/ActivityImportReview.jsx'
import Onsites from './pages/Onsites.jsx'
import OnsiteForm from './pages/OnsiteForm.jsx'
import OnsiteDetail from './pages/OnsiteDetail.jsx'
import CustomerForm from './pages/CustomerForm.jsx'
import CustomerProfile from './pages/CustomerProfile.jsx'
import Franchisees from './pages/Franchisees.jsx'
import Tests from './pages/Tests.jsx'
import TestForm from './pages/TestForm.jsx'
import Users from './pages/Users.jsx'
import UserNew from './pages/UserNew.jsx'
import Settings from './pages/Settings.jsx'
import Reports from './pages/Reports.jsx'
import AuditLog from './pages/AuditLog.jsx'
import PublicOnsiteReport from './pages/PublicOnsiteReport.jsx'
import FeedbackButton from './feedback/FeedbackButton.jsx'

// The primitive kitchensink exists only in development builds. The lazy
// import behind the DEV check keeps it out of the production bundle.
const Kitchensink = import.meta.env.DEV ? lazy(() => import('./dev/Kitchensink.jsx')) : null

// The sidebar glyph for each navigation destination. Purely presentation;
// the items themselves still come from the permission map.
const NAV_ICONS = {
  '/activity': Rows3,
  '/customers': Building2,
  '/onsites': CalendarCheck,
  '/franchisees': Landmark,
  '/tests': FlaskConical,
  '/reports': FileBarChart,
  '/admin/users': UsersIcon,
  '/admin/settings': SettingsIcon,
}

// The top bar names the section the current route belongs to, in this
// role's words for it.
function titleFor(nav, pathname) {
  const view = viewForPath(pathname)
  return nav.find((item) => item.to === view?.path)?.label ?? ''
}

function Shell({ children }) {
  const { user, signOut, nav } = useAuth()
  const { theme, setTheme, density, setDensity } = useTheme()
  const location = useLocation()

  return (
    <AppShell
      brand="National Accounts"
      nav={nav.map((item) => ({ ...item, icon: NAV_ICONS[item.to] }))}
      title={titleFor(nav, location.pathname)}
      actions={
        <>
          <ViewAs />
          <OnsitesOpt />
          <Button
            variant="icon"
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
            title={theme === 'light' ? 'Dark theme' : 'Light theme'}
          >
            {theme === 'light' ? (
              <Moon size={16} strokeWidth={1.75} aria-hidden="true" />
            ) : (
              <Sun size={16} strokeWidth={1.75} aria-hidden="true" />
            )}
          </Button>
          <Button
            variant="icon"
            onClick={() => setDensity(density === 'normal' ? 'dense' : 'normal')}
            aria-label={density === 'normal' ? 'Switch to dense layout' : 'Switch to normal layout'}
            aria-pressed={density === 'dense'}
            title={density === 'normal' ? 'Dense layout' : 'Normal layout'}
          >
            <Rows3 size={16} strokeWidth={1.75} aria-hidden="true" />
          </Button>
          <span className="who-name" title={user?.email}>
            {user?.displayName}
          </span>
          <Button variant="ghost" onClick={signOut}>
            Sign out
          </Button>
        </>
      }
    >
      {children}

      {/* Removable feedback widget; renders nothing without the grant. */}
      <FeedbackButton />
    </AppShell>
  )
}

function RequireAuth({ children }) {
  const { user } = useAuth()
  const location = useLocation()

  if (!user) {
    const next = `${location.pathname}${location.search}`
    return <Navigate to={`/signin?next=${encodeURIComponent(next)}`} replace />
  }
  return children
}

// Renders the page only if the role holds the view. The server refuses the
// data either way, so this exists to show a sentence instead of a broken screen.
function RequireView({ view, children }) {
  const { canView, homePath } = useAuth()
  if (!canView(view)) {
    return (
      <Notice tone="bad">
        That page is not part of your access. <Link to={homePath}>Go back</Link>.
      </Notice>
    )
  }
  return children
}

// The root has no page of its own. Every role lands on its leftmost view,
// which is the activity dashboard.
function RoleHome() {
  const { homePath } = useAuth()
  return <Navigate to={homePath} replace />
}

function page(element) {
  return (
    <RequireAuth>
      <Shell>{element}</Shell>
    </RequireAuth>
  )
}

// A page behind a view grant. The view key is the one in shared/permissions.js.
function viewPage(view, element) {
  return page(<RequireView view={view}>{element}</RequireView>)
}

export default function App() {
  const { loading, user } = useAuth()

  if (loading) return <div className="center-note">Loading</div>

  return (
    <ConfirmProvider>
      <Routes>
        <Route path="/signin" element={user ? <Navigate to="/" replace /> : <SignIn />} />
        {/* Public by design: the token in the URL is the credential, and the
            server enforces it. No shell, no session. */}
        <Route path="/submit/:token" element={<PublicOnsiteReport />} />
        {Kitchensink ? (
          <Route
            path="/dev/ui"
            element={
              <Suspense fallback={null}>
                <Kitchensink />
              </Suspense>
            }
          />
        ) : null}
        <Route
          path="/"
          element={
            <RequireAuth>
              <RoleHome />
            </RequireAuth>
          }
        />
        <Route path="/customers" element={viewPage('customers', <Customers />)} />
        <Route path="/customers/new" element={viewPage('customers', <CustomerForm />)} />
        <Route path="/customers/:id" element={viewPage('customers', <CustomerProfile />)} />
        <Route path="/customers/:id/edit" element={viewPage('customers', <CustomerForm />)} />
        <Route path="/activity" element={viewPage('activity', <Activity />)} />
        <Route
          path="/activity/customers/:id"
          element={viewPage('activity', <ActivityCustomer />)}
        />
        <Route path="/activity/imports" element={viewPage('activity', <ActivityImports />)} />
        <Route
          path="/activity/imports/:id"
          element={viewPage('activity', <ActivityImportReview />)}
        />
        <Route path="/onsites" element={viewPage('onsites', <Onsites />)} />
        <Route path="/onsites/new" element={viewPage('onsites', <OnsiteForm />)} />
        <Route path="/onsites/:id" element={viewPage('onsites', <OnsiteDetail />)} />
        <Route path="/onsites/:id/edit" element={viewPage('onsites', <OnsiteForm />)} />
        <Route path="/reports" element={viewPage('reports', <Reports />)} />
        <Route path="/reports/audit" element={viewPage('reports', <AuditLog />)} />
        <Route path="/franchisees" element={viewPage('franchisees', <Franchisees />)} />
        <Route path="/tests" element={viewPage('tests', <Tests />)} />
        <Route path="/tests/new" element={viewPage('tests', <TestForm />)} />
        <Route path="/tests/:id/edit" element={viewPage('tests', <TestForm />)} />
        <Route path="/admin/users" element={viewPage('users', <Users />)} />
        <Route path="/admin/users/new" element={viewPage('users', <UserNew />)} />
        <Route path="/admin/settings" element={viewPage('settings', <Settings />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ConfirmProvider>
  )
}
